<template>
    <div class="slider range-slider">
        <input type="text" :value="first" @input="inputFirst" />
        <div class="track-container">
            <div class="track" ref="track">
                <div class="track-unselected-range" :style="`left:0;width:${rangeMinPercentage}%`"></div>
                <div
                    class="track-selected-range"
                    :style="`left:${rangeMinPercentage}%;width:${rangeMaxPercentage - rangeMinPercentage}%`"
                ></div>
                <div
                    class="track-unselected-range"
                    :style="`left:${rangeMaxPercentage}%;width:${100 - rangeMaxPercentage}%`"
                ></div>
            </div>
            <div class="thumb-container">
                <SliderThumb v-model="firstThumb" />
                <SliderThumb v-model="secondThumb" />
            </div>
        </div>
        <input type="text" :value="second" @input="inputSecond" />
    </div>
</template>
<script>
import debounce from 'debounce'

import SliderThumb from './slider-thumb.vue'

export default {
    model: {
        prop: 'modelValue',
        event: 'update:modelValue',
    },
    components: {
        SliderThumb,
    },
    props: {
        modelValue: {
            type: Array,
            default() {
                return [0, 100]
            },
        },
        max: {
            type: [Number, String],
            default: 100,
        },
        min: {
            type: [Number, String],
            default: 0,
        },
        step: {
            type: [Number, String],
            default: 1,
        },
    },
    data() {
        return {}
    },
    computed: {
        first: {
            get() {
                return this.modelValue[0]
            },
            set(value) {
                value = parseFloat(value)
                value = !isNaN(value) ? value : this.first
                const bounded = this.applyBounds(value)
                this.$emit('update:modelValue', this.second < bounded ? [this.second, bounded] : [bounded, this.second])
            },
        },
        second: {
            get() {
                return this.modelValue[1]
            },
            set(value) {
                value = parseFloat(value)
                value = !isNaN(value) ? value : this.second
                const bounded = this.applyBounds(value)
                this.$emit('update:modelValue', this.first < bounded ? [this.first, bounded] : [bounded, this.first])
            },
        },
        firstThumb: {
            get() {
                return this.thumbValue(this.first)
            },
            set(thumbValue) {
                this.first = this.scaleThumb(thumbValue)
            },
        },
        secondThumb: {
            get() {
                return this.thumbValue(this.second)
            },
            set(thumbValue) {
                this.second = this.scaleThumb(thumbValue)
            },
        },
        rangeMaxPercentage() {
            return ((Math.max(this.first, this.second) - this.minValue) / (this.maxValue - this.minValue)) * 100
        },
        rangeMinPercentage() {
            return ((Math.min(this.first, this.second) - this.minValue) / (this.maxValue - this.minValue)) * 100
        },
        minValue() {
            return parseFloat(this.min)
        },
        maxValue() {
            return parseFloat(this.max)
        },
        stepNumeric() {
            return this.step > 0 ? parseFloat(this.step) : 0
        },
    },
    watch: {
        min(val) {
            var first = this.applyBounds(this.first)
            var second = this.applyBounds(this.second)
            this.$emit('update:modelValue', [first, second])
        },
        max(val) {
            var first = this.applyBounds(this.first)
            var second = this.applyBounds(this.second)
            this.$emit('update:modelValue', [first, second])
        },
    },
    methods: {
        thumbValue(value) {
            return (value - this.minValue) / (this.maxValue - this.minValue)
        },
        scaleThumb(value) {
            return value * (this.maxValue - this.minValue) + this.minValue
        },
        applyBounds(value) {
            return Math.min(this.maxValue, Math.max(this.minValue, this.roundValue(value)))
        },
        roundValue(value) {
            if (!this.stepNumeric) return value
            // Format input value using the same number
            // of decimals places as in the step prop
            const trimmedStep = this.step.toString().trim()
            const decimals = trimmedStep.indexOf('.') > -1 ? trimmedStep.length - trimmedStep.indexOf('.') - 1 : 0
            const offset = this.minValue % this.stepNumeric
            const newValue = Math.round((value - offset) / this.stepNumeric) * this.stepNumeric + offset
            return parseFloat(Math.min(newValue, this.maxValue).toFixed(decimals))
        },
        inputFirst: debounce(function (value) {
            // This might be a raw value emited by the thumb or an input event
            value = parseFloat(value?.target?.value ?? value)
            this.first = value
        }, 500),
        inputSecond: debounce(function (value) {
            // This might be a raw value emited by the thumb or an input event
            value = parseFloat(value?.target?.value ?? value)
            this.second = value
        }, 500),
    },
}
</script>
<style src="./slider.css" />
